import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Phase 3.7 Tier 5 — partner doc show page.
//
// Three jobs:
//   1) Fire "Partner Doc Viewed" exactly once per page load (client-side
//      per the plan; mirrors the partner-usage controller pattern).
//   2) Highlight the on-this-page TOC entry whose section is currently
//      in view (commonmarker emits anchors as
//      `<h2><a class="anchor" id="…"></a>Title</h2>` so we read the
//      anchor's id, not the heading itself).
//   3) Capture the "Was this helpful?" thumbs-up/down vote. The vote
//      emits a Mixpanel event and disables the buttons — no DB
//      persistence per the plan; signal lives in product analytics.
export default class extends Controller {
  static targets = ["toc", "feedbackPanel", "feedbackThanks", "upButton", "downButton"]
  static values = {
    slug: String,
    category: String,
    title: String
  }

  connect() {
    this._loadedAt = Date.now()

    track("Partner Doc Viewed", {
      slug: this.slugValue,
      category: this.categoryValue,
      title: this.titleValue
    })

    this._wireTocHighlight()
  }

  voteUp(event) { this._submitVote(event, "up") }
  voteDown(event) { this._submitVote(event, "down") }

  _submitVote(event, vote) {
    event.preventDefault()
    if (this._voted) return
    this._voted = true

    const seconds = Math.round((Date.now() - this._loadedAt) / 1000)
    track("Partner Doc Helpful Voted", {
      slug: this.slugValue,
      category: this.categoryValue,
      vote,
      seconds_on_page: seconds
    })

    if (this.hasUpButtonTarget) {
      this.upButtonTarget.disabled = true
      this.upButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
    if (this.hasDownButtonTarget) {
      this.downButtonTarget.disabled = true
      this.downButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
    if (this.hasFeedbackThanksTarget) {
      this.feedbackThanksTarget.classList.remove("hidden")
    }
  }

  _wireTocHighlight() {
    if (!this.hasTocTarget) return

    const anchors = Array.from(
      document.querySelectorAll('article.partner-doc h2 a.anchor[id], article.partner-doc h3 a.anchor[id]')
    )
    const links = Array.from(this.tocTarget.querySelectorAll('a[data-toc-slug]'))
    if (!anchors.length || !links.length) return

    const linkBySlug = {}
    links.forEach(a => { linkBySlug[a.dataset.tocSlug] = a })

    const update = () => {
      const offset = 80
      let current = null
      for (let i = 0; i < anchors.length; i++) {
        const top = anchors[i].getBoundingClientRect().top
        if (top - offset <= 0) current = anchors[i]
        else break
      }
      links.forEach(a => {
        a.classList.remove("text-brand", "border-brand")
        a.classList.add("text-gray-600", "border-transparent")
      })
      if (current && linkBySlug[current.id]) {
        const active = linkBySlug[current.id]
        active.classList.remove("text-gray-600", "border-transparent")
        active.classList.add("text-brand", "border-brand")
      }
    }

    update()
    this._tocScrollHandler = update
    document.addEventListener("scroll", update, { passive: true })
  }

  disconnect() {
    if (this._tocScrollHandler) {
      document.removeEventListener("scroll", this._tocScrollHandler)
    }
  }
}
